import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Drawer, Icon, IconName, Select, SelectItem } from './ui';
import { useTranslation } from 'react-i18next';

/**
 * Policy Studio — the unified policy editor. One screen authors a single policy
 * spanning every governance domain as composable, independently-toggled blocks:
 * MCP tool access, data protection (DLP), prompt-injection/threat, token budget,
 * and model routing. Saves to /admin/policies with a `blocks` object; the server
 * keeps the legacy access `effect`/`conditions` in sync for enforcement.
 */

type Effect = 'allow' | 'deny';
type SubjectType = 'agent' | 'group' | 'role' | 'global';

interface MCPService {
  name: string;
  tools?: { name: string; description?: string }[];
}

interface ModelInfo {
  id: string;
  label: string;
  provider: string;
  enabled?: boolean;
}

interface ProviderInfo {
  id: string;
  label: string;
  kind: 'cloud' | 'self-hosted';
  enabled?: boolean;
}

interface CredentialInfo {
  credential_id: string;
  provider: string;
  label: string;
  department: string | null;
  key_masked: string | null;
  is_default: boolean;
  enabled: boolean;
}

interface AgentLite {
  agent_id: string;
  name: string;
  role: string;
}

interface Blocks {
  access: { enabled: boolean; effect: Effect; tools: string[]; time_window?: { start: string; end: string }; expr?: string; approval_required_tools?: string[] };
  data_protection: { enabled: boolean; mode: 'redact' | 'block' | 'tokenize'; categories: string[]; inspect_request: boolean; inspect_response: boolean };
  threat: { enabled: boolean; injection_block: boolean; canary: boolean };
  budget: { enabled: boolean; tokens: number; breach_action: 'warn' | 'downgrade' | 'block'; warn_threshold_pct: number };
  routing: { enabled: boolean; primary_model: string; downgrade_model: string; provider: string; credential_id: string };
}

interface PolicyLike {
  policy_id: string;
  name: string;
  description?: string;
  effect: Effect;
  conditions: { tools?: string[]; time_window?: { start: string; end: string }; expr?: string };
  tags?: string[];
  blocks?: Partial<Blocks>;
}

interface BindingDraft {
  subject_type: SubjectType;
  subject_ref: string;
  priority: number;
}

const DLP_CATEGORIES = ['SSN', 'EMAIL', 'CREDIT_CARD', 'PHONE', 'API_KEY', 'AWS_KEY', 'PASSWORD'];

const emptyBlocks = (): Blocks => ({
  access: { enabled: true, effect: 'allow', tools: ['*'], approval_required_tools: [] },
  data_protection: { enabled: false, mode: 'redact', categories: ['SSN', 'CREDIT_CARD'], inspect_request: true, inspect_response: false },
  threat: { enabled: false, injection_block: true, canary: false },
  budget: { enabled: false, tokens: 500000, breach_action: 'downgrade', warn_threshold_pct: 80 },
  routing: { enabled: false, primary_model: '', downgrade_model: '', provider: '', credential_id: '' },
});

const mergeBlocks = (p?: Partial<Blocks>): Blocks => {
  const base = emptyBlocks();
  if (!p) return base;
  return {
    access: { ...base.access, ...(p.access || {}) },
    data_protection: { ...base.data_protection, ...(p.data_protection || {}) },
    threat: { ...base.threat, ...(p.threat || {}) },
    budget: { ...base.budget, ...(p.budget || {}) },
    routing: { ...base.routing, ...(p.routing || {}) },
  };
};

interface Props {
  apiUrl: string;
  editing: PolicyLike | null;
  mcpServices: MCPService[];
  onClose: () => void;
  onSaved: () => void;
}

const PolicyStudio: React.FC<Props> = ({ apiUrl, editing, mcpServices, onClose, onSaved }) => {
  const { t } = useTranslation();
  const { t: tr } = useTranslation();
  const [name, setName] = useState(editing?.name || '');
  const [description, setDescription] = useState(editing?.description || '');
  const [tags, setTags] = useState<string[]>(editing?.tags || []);
  const [tagDraft, setTagDraft] = useState('');
  const [blocks, setBlocks] = useState<Blocks>(() =>
    mergeBlocks(
      editing?.blocks && Object.keys(editing.blocks).length
        ? editing.blocks
        : editing
        ? { access: { enabled: true, effect: editing.effect, tools: editing.conditions.tools || ['*'], time_window: editing.conditions.time_window, expr: editing.conditions.expr } }
        : undefined
    )
  );
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [credentials, setCredentials] = useState<CredentialInfo[]>([]);
  const [serverSel, setServerSel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agents, setAgents] = useState<AgentLite[]>([]);
  const [showBindingSection, setShowBindingSection] = useState(false);
  const [binding, setBinding] = useState<BindingDraft>({ subject_type: 'global', subject_ref: '', priority: 0 });

  useEffect(() => {
    fetch(`${apiUrl}/admin/gateway/models`)
      .then((r) => r.json())
      .then((d) => {
        setModels((d.models || []).filter((m: ModelInfo) => m.enabled !== false));
        setProviders((d.providers || []).filter((p: ProviderInfo) => p.enabled !== false));
      })
      .catch(() => {});
    fetch(`${apiUrl}/admin/providers/credentials`)
      .then((r) => r.json())
      .then((d) => setCredentials((d.credentials || []).filter((c: CredentialInfo) => c.enabled !== false)))
      .catch(() => {});
    fetch(`${apiUrl}/admin/agents`)
      .then((r) => r.json())
      .then((d) => setAgents((d.agents || []).map((a: any) => ({ agent_id: a.agent_id, name: a.name, role: a.role }))))
      .catch(() => {});
  }, [apiUrl]);

  const toolsForServer = useMemo(() => {
    const s = mcpServices.find((x) => x.name === serverSel);
    return s?.tools ? ['*', ...s.tools.map((t) => `${serverSel}.${t.name}`)] : [];
  }, [serverSel, mcpServices]);

  // Keys for the chosen routing provider (default-first), and models scoped to
  // that provider once one is picked (else the full catalog).
  const credsForProvider = useMemo(
    () => credentials.filter((c) => c.provider === blocks.routing.provider),
    [credentials, blocks.routing.provider]
  );
  const routingModels = useMemo(
    () => (blocks.routing.provider ? models.filter((m) => m.provider === blocks.routing.provider) : models),
    [models, blocks.routing.provider]
  );

  const patch = <K extends keyof Blocks>(k: K, v: Partial<Blocks[K]>) =>
    setBlocks((b) => ({ ...b, [k]: { ...b[k], ...v } }));

  const toggle = (k: keyof Blocks) => setBlocks((b) => ({ ...b, [k]: { ...b[k], enabled: !b[k].enabled } }));

  const addTool = (t: string) => {
    if (!t) return;
    setBlocks((b) => (b.access.tools.includes(t) ? b : { ...b, access: { ...b.access, tools: [...b.access.tools.filter((x) => x !== '*' || t === '*'), t] } }));
  };
  const removeTool = (t: string) =>
    patch('access', {
      tools: blocks.access.tools.filter((x) => x !== t),
      approval_required_tools: (blocks.access.approval_required_tools || []).filter((x) => x !== t),
    });
  const toggleApprovalRequired = (t: string) => {
    const current = blocks.access.approval_required_tools || [];
    patch('access', {
      approval_required_tools: current.includes(t) ? current.filter((x) => x !== t) : [...current, t],
    });
  };

  const addTag = () => {
    const t = tagDraft.trim().slice(0, 40);
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setTagDraft('');
  };

  // Remembers a policy created during THIS drawer session, so if a later step
  // (the binding) fails, a retry UPDATES that policy instead of POSTing a new one
  // with the same name — which the server rejects (UNIQUE org_id+name → 409).
  const createdPolicyId = useRef<string | null>(null);

  const save = async () => {
    setError(null);
    if (!name.trim()) {
      setError('Policy name is required');
      return;
    }
    const enabledCount = Object.values(blocks).filter((x) => x.enabled).length;
    if (enabledCount === 0) {
      setError('Enable at least one rule block');
      return;
    }
    // Validate the binding BEFORE writing anything, so an incomplete binding can
    // never leave a half-saved (orphan) policy behind.
    if (showBindingSection && binding.subject_type !== 'global' && !binding.subject_ref) {
      setError('Select an agent/group/role for the binding');
      return;
    }
    setSaving(true);
    try {
      // Update when editing an existing policy OR retrying after we already
      // created one this session; otherwise create.
      const targetId = editing?.policy_id || createdPolicyId.current;
      const method = targetId ? 'PUT' : 'POST';
      const url = targetId ? `${apiUrl}/admin/policies/${targetId}` : `${apiUrl}/admin/policies`;
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          description,
          effect: blocks.access.effect,
          tags,
          blocks,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || d.error || 'Failed to save policy');
      }
      const policy = await res.json();
      const policyId = policy.policy_id || policy.policy?.policy_id || targetId;
      // Record it so any retry this session updates rather than re-creates.
      if (!editing) createdPolicyId.current = policyId;

      // If binding is configured, create it
      if (showBindingSection) {
        const subjectRef = binding.subject_type === 'global' ? null : binding.subject_ref;
        const bindRes = await fetch(`${apiUrl}/admin/policy-bindings`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            policy_id: policyId,
            subject_type: binding.subject_type,
            subject_ref: subjectRef,
            priority: binding.priority,
          }),
        });
        if (!bindRes.ok) {
          const d = await bindRes.json().catch(() => ({}));
          throw new Error(d.message || d.error || 'Failed to create binding');
        }
      }
      onSaved();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const blockMeta: Record<keyof Blocks, { icon: IconName; title: string; color: string; subtitle: string }> = {
    access: { icon: 'lock', title: t('components.policyStudio.mcpToolAccess'), color: '#185FA5', subtitle: t('components.policyStudio.blockToolsSub') },
    data_protection: { icon: 'shield', title: t('components.policyStudio.dataProtectionDLP'), color: '#0F6E56', subtitle: t('components.policyStudio.blockPiiSub') },
    threat: { icon: 'bolt', title: t('components.policyStudio.promptInjectionThreat'), color: '#A32D2D', subtitle: t('components.policyStudio.blockJailbreakSub') },
    budget: { icon: 'key', title: t('components.policyStudio.tokenBudget'), color: '#BA7517', subtitle: t('components.policyStudio.blockCapsSub') },
    routing: { icon: 'signal', title: t('components.policyStudio.modelRouting'), color: '#534AB7', subtitle: t('components.policyStudio.blockAutoSwitchSub') },
  };

  const BlockCard: React.FC<{ k: keyof Blocks; children: React.ReactNode }> = ({ k, children }) => {
    const m = blockMeta[k];
    const on = blocks[k].enabled;
    return (
      <div style={{ border: '1px solid var(--border)', borderRadius: 10, marginBottom: 12, overflow: 'hidden', opacity: on ? 1 : 0.65 }}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--surface-2)', cursor: 'pointer' }}
          onClick={() => toggle(k)}
        >
          <Icon name={m.icon} size={18} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>{m.title}</div>
            <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{m.subtitle}</div>
          </div>
          <span
            role="switch"
            aria-checked={on}
            style={{
              width: 38, height: 20, borderRadius: 12, background: on ? m.color : 'var(--border-strong)',
              position: 'relative', transition: 'background .15s', flexShrink: 0,
            }}
          >
            <span style={{ position: 'absolute', top: 2, left: on ? 20 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
          </span>
        </div>
        {on && <div style={{ padding: '12px 14px' }}>{children}</div>}
      </div>
    );
  };

  const fieldRow: React.CSSProperties = { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' };
  const lbl: React.CSSProperties = { display: 'flex', flexDirection: 'column', fontSize: 12, gap: 4 };

  return (
    <Drawer
      title={editing ? tr('components.policyStudio.editPolicy') : tr('components.policyStudio.newPolicy')}
      width={640}
      onClose={onClose}
      footer={
        <>
          <button className="ent-btn" onClick={onClose}>{tr('components.policyStudio.cancel')}</button>
          <button className="ent-btn primary" onClick={save} disabled={saving}>
            <Icon name="checkCircle" /> {editing ? tr('components.policyStudio.saveChanges') : tr('components.policyStudio.createPolicy')}
          </button>
        </>
      }
    >
      <div style={{ paddingBottom: 8 }}>
        {error && (
          <div className="notice error" style={{ marginBottom: 12 }}>
            <Icon name="warning" /> {error}
          </div>
        )}

        <div className="ent-field">
          <label>{tr('components.policyStudio.policyName')}</label>
          <input className="ent-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={tr('components.policyStudio.eGFinanceAgentsGuarded')} />
        </div>
        <div className="ent-field">
          <label>{tr('components.policyStudio.description')}</label>
          <input className="ent-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={tr('components.policyStudio.whatThisPolicyGoverns')} />
        </div>
        <div className="ent-field">
          <label>{tr('components.policyStudio.tags')}</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {tags.map((t) => (
              <span key={t} className="tag-pill active" style={{ cursor: 'default' }}>
                {t}{' '}
                <span onClick={() => setTags(tags.filter((x) => x !== t))} style={{ cursor: 'pointer' }}>×</span>
              </span>
            ))}
            <input
              className="ent-input"
              style={{ width: 140 }}
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
              placeholder={tr('components.policyStudio.addTag')}
            />
          </div>
        </div>

        <div style={{ fontSize: 12, color: 'var(--text-2)', margin: '16px 0 8px', textTransform: 'uppercase', letterSpacing: '.04em' }}>
          {tr('components.policyStudio.ruleBlocksToggleToCompose')}
        </div>

        {/* ---- MCP tool access ---- */}
        <BlockCard k="access">
          <div style={fieldRow}>
            <label style={lbl}>
              {tr('components.policyStudio.effect')}
              <Select value={blocks.access.effect} onValueChange={(v) => patch('access', { effect: v as Effect })}>
                <SelectItem value="allow">{tr('components.policyStudio.allow')}</SelectItem>
                <SelectItem value="deny">{tr('components.policyStudio.deny')}</SelectItem>
              </Select>
            </label>
            <label style={lbl}>
              {tr('components.policyStudio.server')}
              <Select value={serverSel} onValueChange={setServerSel} placeholder={tr('components.policyStudio.selectServer')}>
                {mcpServices.map((s) => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}
              </Select>
            </label>
            <label style={lbl}>
              {tr('components.policyStudio.tool')}
              <Select value="" onValueChange={addTool} placeholder={tr('components.policyStudio.addTool')} disabled={!serverSel}>
                {toolsForServer.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </Select>
            </label>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
            {blocks.access.tools.map((t) => (
              <span key={t} className="tag-pill active" style={{ cursor: 'default' }}>
                {t} <span onClick={() => removeTool(t)} style={{ cursor: 'pointer' }}>×</span>
              </span>
            ))}
          </div>
          {blocks.access.effect === 'allow' && blocks.access.tools.length > 0 && blocks.access.tools[0] !== '*' && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 6 }}>
                {tr('components.policyStudio.requireHumanApprovalBeforeThisTool')}
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {blocks.access.tools.map((t) => (
                  <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={(blocks.access.approval_required_tools || []).includes(t)}
                      onChange={() => toggleApprovalRequired(t)}
                    />
                    {t}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div style={{ ...fieldRow, marginTop: 12 }}>
            <label style={lbl}>
              {tr('components.policyStudio.activeFrom')}
              <input className="ent-input" type="time" value={blocks.access.time_window?.start || ''} onChange={(e) => patch('access', { time_window: { start: e.target.value, end: blocks.access.time_window?.end || '' } })} />
            </label>
            <label style={lbl}>
              {tr('components.policyStudio.to')}
              <input className="ent-input" type="time" value={blocks.access.time_window?.end || ''} onChange={(e) => patch('access', { time_window: { start: blocks.access.time_window?.start || '', end: e.target.value } })} />
            </label>
          </div>
        </BlockCard>

        {/* ---- Data protection ---- */}
        <BlockCard k="data_protection">
          <div style={fieldRow}>
            <label style={lbl}>
              {tr('components.policyStudio.mode')}
              <Select value={blocks.data_protection.mode} onValueChange={(v) => patch('data_protection', { mode: v as any })}>
                <SelectItem value="redact">{tr('components.policyStudio.redact')}</SelectItem>
                <SelectItem value="block">{tr('components.policyStudio.block')}</SelectItem>
                <SelectItem value="tokenize">{tr('components.policyStudio.tokenize')}</SelectItem>
              </Select>
            </label>
            <label style={{ ...lbl, flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={blocks.data_protection.inspect_request} onChange={(e) => patch('data_protection', { inspect_request: e.target.checked })} />
              {tr('components.policyStudio.inspectPrompt')}
            </label>
            <label style={{ ...lbl, flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={blocks.data_protection.inspect_response} onChange={(e) => patch('data_protection', { inspect_response: e.target.checked })} />
              {tr('components.policyStudio.inspectResponse')}
            </label>
          </div>
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 6 }}>{tr('components.policyStudio.detectors')}</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {DLP_CATEGORIES.map((c) => {
                const on = blocks.data_protection.categories.includes(c);
                return (
                  <span
                    key={c}
                    onClick={() => patch('data_protection', { categories: on ? blocks.data_protection.categories.filter((x) => x !== c) : [...blocks.data_protection.categories, c] })}
                    className={`tag-pill ${on ? 'active' : ''}`}
                    style={{ cursor: 'pointer' }}
                  >
                    {c}
                  </span>
                );
              })}
            </div>
          </div>
        </BlockCard>

        {/* ---- Threat ---- */}
        <BlockCard k="threat">
          <div style={fieldRow}>
            <label style={lbl}>
              {tr('components.policyStudio.onDetection')}
              <Select
                value={blocks.threat.injection_block ? 'block' : 'flag'}
                onValueChange={(v) => patch('threat', { injection_block: v === 'block' })}
              >
                <SelectItem value="block">{tr('components.policyStudio.blockRequest')}</SelectItem>
                <SelectItem value="flag">{tr('components.policyStudio.flagAuditOnly')}</SelectItem>
              </Select>
            </label>
            <label style={{ ...lbl, flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={blocks.threat.canary} onChange={(e) => patch('threat', { canary: e.target.checked })} />
              {tr('components.policyStudio.injectCanaryTripwires')}
            </label>
          </div>
        </BlockCard>

        {/* ---- Budget ---- */}
        <BlockCard k="budget">
          <div style={fieldRow}>
            <label style={lbl}>
              {tr('components.policyStudio.monthlyTokens')}
              <input className="ent-input" type="number" value={blocks.budget.tokens} onChange={(e) => patch('budget', { tokens: Number(e.target.value) || 0 })} style={{ width: 120 }} />
            </label>
            <label style={lbl}>
              {tr('components.policyStudio.onBreach')}
              <Select value={blocks.budget.breach_action} onValueChange={(v) => patch('budget', { breach_action: v as any })}>
                <SelectItem value="warn">{tr('components.policyStudio.warn')}</SelectItem>
                <SelectItem value="downgrade">{tr('components.policyStudio.downgrade')}</SelectItem>
                <SelectItem value="block">{tr('components.policyStudio.block')}</SelectItem>
              </Select>
            </label>
            <label style={lbl}>
              {tr('components.policyStudio.warnAt')}
              <input className="ent-input" type="number" value={blocks.budget.warn_threshold_pct} onChange={(e) => patch('budget', { warn_threshold_pct: Number(e.target.value) || 0 })} style={{ width: 70 }} />
            </label>
          </div>
        </BlockCard>

        {/* ---- Routing ---- */}
        <BlockCard k="routing">
          <div style={fieldRow}>
            <label style={lbl}>
              {tr('components.policyStudio.provider')}
              <Select
                value={blocks.routing.provider}
                onValueChange={(v) => patch('routing', { provider: v, credential_id: '' })}
                placeholder={tr('components.policyStudio.defaultEnv')}
              >
                <SelectItem value="__default__">{tr('components.policyStudio.defaultEnv')}</SelectItem>
                {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              </Select>
            </label>
            <label style={lbl}>
              {tr('components.policyStudio.apiKey')}
              <Select
                value={blocks.routing.credential_id}
                onValueChange={(v) => patch('routing', { credential_id: v })}
                disabled={!blocks.routing.provider}
                placeholder={blocks.routing.provider ? tr('components.policyStudio.providerDefaultKey') : '—'}
              >
                {credsForProvider.map((c) => (
                  <SelectItem key={c.credential_id} value={c.credential_id}>
                    {c.label}{c.department ? ` · ${c.department}` : ''} ({c.key_masked || t('components.policyStudio.noKey')}){c.is_default ? t('components.policyStudio.defaultBadge') : ''}
                  </SelectItem>
                ))}
              </Select>
            </label>
          </div>
          {blocks.routing.provider && credsForProvider.length === 0 && (
            <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 6, fontStyle: 'italic' }}>
              {tr('components.policyStudio.noKeysStoredForThisProvider')}
            </div>
          )}
          <div style={{ ...fieldRow, marginTop: 12 }}>
            <label style={lbl}>
              {tr('components.policyStudio.primaryModel')}
              <Select value={blocks.routing.primary_model} onValueChange={(v) => patch('routing', { primary_model: v })} placeholder={tr('components.policyStudio.select')}>
                {routingModels.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
              </Select>
            </label>
            <label style={lbl}>
              {tr('components.policyStudio.downgradeTo')}
              <Select value={blocks.routing.downgrade_model} onValueChange={(v) => patch('routing', { downgrade_model: v })} placeholder={tr('components.policyStudio.select')}>
                {routingModels.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
              </Select>
            </label>
          </div>
        </BlockCard>

        {/* ---- Bindings (optional) ---- */}
        <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={() => setShowBindingSection(!showBindingSection)}>
            <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>{tr('components.policyStudio.whoDoesThisPolicyApplyTo')}</div>
            <Icon name={showBindingSection ? 'chevronDown' : 'chevronRight'} size={16} style={{ color: 'var(--text-2)' }} />
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4 }}>{tr('components.policyStudio.optionalAssignThisPolicyToAgents')}</div>

          {showBindingSection && (
            <div style={{ marginTop: 12, padding: '12px 14px', background: 'var(--surface-2)', borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={fieldRow}>
                <label style={lbl}>
                  {tr('components.policyStudio.scope')}
                  <Select value={binding.subject_type} onValueChange={(v) => setBinding({ ...binding, subject_type: v as SubjectType, subject_ref: '' })}>
                    <SelectItem value="global">{tr('components.policyStudio.globalCatchAll')}</SelectItem>
                    <SelectItem value="agent">{tr('components.policyStudio.agent')}</SelectItem>
                    <SelectItem value="group">{tr('components.policyStudio.groupIDM')}</SelectItem>
                    <SelectItem value="role">{tr('components.policyStudio.role')}</SelectItem>
                  </Select>
                </label>
                {binding.subject_type !== 'global' && (
                  <label style={lbl}>
                    {binding.subject_type === 'agent' ? t('components.policyStudio.selectAgent') : binding.subject_type === 'group' ? t('components.policyStudio.groupId') : t('components.policyStudio.role')}
                    {binding.subject_type === 'agent' ? (
                      <Select value={binding.subject_ref} onValueChange={(v) => setBinding({ ...binding, subject_ref: v })} placeholder={tr('components.policyStudio.select')}>
                        {agents.map((a) => <SelectItem key={a.agent_id} value={a.agent_id}>{a.name} ({a.role})</SelectItem>)}
                      </Select>
                    ) : (
                      <input className="ent-input" value={binding.subject_ref} onChange={(e) => setBinding({ ...binding, subject_ref: e.target.value })} placeholder={tr('components.policyStudio.eGFinanceTeamOrAdmin')} />
                    )}
                  </label>
                )}
                <label style={lbl}>
                  {tr('components.policyStudio.priorityHigherWins')}
                  <input className="ent-input" type="number" value={binding.priority} onChange={(e) => setBinding({ ...binding, priority: Number(e.target.value) || 0 })} style={{ width: 70 }} />
                </label>
              </div>
              {binding.subject_type === 'global' && (
                <div style={{ fontSize: 11, color: 'var(--text-2)', fontStyle: 'italic' }}>{tr('components.policyStudio.thisPolicyWillBeTheDefault')}</div>
              )}
              {binding.subject_type === 'agent' && agents.length > 0 && (
                <div style={{ fontSize: 11, color: 'var(--text-2)' }}>{tr('components.policyStudio.affects')} <strong>{agents.find(a => a.agent_id === binding.subject_ref)?.name || tr('components.policyStudio.noneSelected')}</strong></div>
              )}
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default PolicyStudio;
